import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  InputSignal,
  Signal,
} from '@angular/core';
import { WorkoutMode } from '@kinomap-dev/ng-ui-lib/core';
import { MinMaxStep, SectionModelType } from '../../types/section.type';
import { SectionsEditorService } from '../../services/sections-editor.service';
import { DIFFICULTY_LEVEL_COLORS } from '../../configs/sections-editor/workout-mode-colors.config';
import { getDifficultyLevel, getSectionBlockGradient } from '../../utils/section-difficulty.utils';
import { durationToSeconds } from '../../utils/duration.utils';
import { valueToHeightPercent } from '../../utils/sections-editor.utils';

type WorkoutPreviewBlock = {
  id: number;
  widthPercent: number;
  heightPercent: number;
  color: string;
  gradient: string;
  secondary: { current: number; previous: number | undefined } | undefined;
};

@Component({
  selector: 'app-workout-preview-chart',
  template: `
    @for (block of blocks(); track block.id) {
      <div class="block" [style.width.%]="block.widthPercent">
        <div class="bar" [style.height.%]="block.heightPercent" [style.background]="block.gradient">
          <div class="border-top" [style.background]="block.color"></div>
        </div>
        @if (block.secondary; as height) {
          <div class="secondary-line" [style.bottom.%]="height.current"></div>
          @if (height.previous !== undefined && height.previous !== height.current) {
            <div
              class="secondary-connector"
              [style.bottom]="'calc(' + Math.min(height.previous, height.current) + '% - 1px)'"
              [style.height]="'calc(' + Math.abs(height.previous - height.current) + '% + 2px)'"
            ></div>
          }
        }
      </div>
    }
  `,
  styleUrl: './workout-preview-chart.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WorkoutPreviewChart {
  private sectionsEditorService: SectionsEditorService = inject(SectionsEditorService);

  sections: InputSignal<SectionModelType[]> = input.required<SectionModelType[]>();
  mode: InputSignal<WorkoutMode | undefined> = input<WorkoutMode>();

  protected readonly Math: Math = Math;

  private readonly totalDuration: Signal<number> = computed(() =>
    this.sections().reduce(
      (total: number, section: SectionModelType) => total + durationToSeconds(section.duration),
      0,
    ),
  );

  readonly blocks: Signal<WorkoutPreviewBlock[]> = computed(() => {
    const total: number = this.totalDuration();
    if (!total) return [];
    const mode: WorkoutMode | undefined = this.mode();
    const heightOf = (data: { type: WorkoutMode; value: number }): number => {
      const { min, max }: Readonly<MinMaxStep> = this.sectionsEditorService.fieldBoundsFor(
        mode,
        data.type,
      );
      return Math.min(100, Math.max(0, valueToHeightPercent(data.value, min, max)));
    };
    const secondaryHeight = (section: SectionModelType | undefined): number | undefined => {
      const data: { type: WorkoutMode; value: number } | undefined =
        section && this.sectionsEditorService.sectionDatas(section, mode)[1];
      return data === undefined ? undefined : heightOf(data);
    };

    return this.sections().map((section: SectionModelType, index: number) => {
      const [primary]: { type: WorkoutMode; value: number }[] =
        this.sectionsEditorService.sectionDatas(section, mode);
      const color: string =
        DIFFICULTY_LEVEL_COLORS[getDifficultyLevel(primary.value, primary.type)];
      const current: number | undefined = secondaryHeight(section);
      return {
        id: section.id,
        widthPercent: (durationToSeconds(section.duration) / total) * 100,
        heightPercent: heightOf(primary),
        color,
        gradient: getSectionBlockGradient(color),
        secondary:
          current === undefined
            ? undefined
            : { current, previous: secondaryHeight(this.sections()[index - 1]) },
      };
    });
  });
}
